import React, { useEffect, useState } from "react";
import Navbar from "../../common/Navbar";
import Footer from "../../common/Footer";
import * as Constants from "../../../Constants/index";
import axios from "axios";
import swal from "sweetalert";
import DatePicker from "react-datepicker";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import { useFormik } from "formik";
import ResponsivePagination from "react-responsive-pagination";
import Oval from "../loader/CircleLoade";
import Modal from "react-modal";
Modal.setAppElement("#root");

export default function FindAnomaly() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [filterData, setFilterData] = useState({});
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  /* --------------------------------------------------------
     Load Initial Data
  --------------------------------------------------------- */
  useEffect(() => {
    getAnomalyData(
      {
        mobile: "",
        status: "PENDING",
        startDate: "",
        endDate: "",
      },
      1
    );
  }, []);

  /* --------------------------------------------------------
     Search Form (Formik)
  --------------------------------------------------------- */
  const searchFormik = useFormik({
    initialValues: {
      mobile: "",
      status: "PENDING",
      startDate: "",
      endDate: "",
    },

    validate: (data) => {
      let errors = {};

      if (data.mobile && data.mobile.length < 10) {
        errors.mobile = "Mobile must be 10 digits.";
      }

      if (data.startDate && !data.endDate) {
        errors.endDate = "End date required";
      }

      if (data.startDate && data.endDate && data.startDate > data.endDate) {
        errors.endDate = "End date should be greater";
      }

      return errors;
    },

    onSubmit: (data) => {
      setCurrentPage(1);
      getAnomalyData(data, 1);
      setFilterData(data);
    },
  });

  const isSearchFormFieldValid = (name) =>
    !!(searchFormik.touched[name] && searchFormik.errors[name]);

  const getSearchFormErrorMessage = (name) => {
    return (
      isSearchFormFieldValid(name) && (
        <small className="report-error-field">{searchFormik.errors[name]}</small>
      )
    );
  };

  /* --------------------------------------------------------
     Fetch Anomaly Data (GET)
  --------------------------------------------------------- */
  const getAnomalyData = async (data, pageNum) => {
    setLoading(true);
    if (!token) navigate("/");
  
    const searchTerm =
      data?.mobile && data.mobile !== "" ? `&searchTerm=${data.mobile}` : "";
  
    const status = data?.status ? `&status=${data.status}` : "&status=PENDING";
  
    let stDate = "";
    let endDate = "";
  
    // ✅ FIXED: Full-day range for filtering
    if (data?.startDate && data?.endDate) {
      stDate = `&start=${moment(data.startDate)
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss")}`;
  
      endDate = `&end=${moment(data.endDate)
        .endOf("day")
        .format("YYYY-MM-DD HH:mm:ss")}`;
    }
  
    axios
      .get(
        Constants.baseUrl +
          `anomaly/findAnomaly?sortBy=createdDateTime&sortAsc=true&limit=${limit}&page=${pageNum}${status}${searchTerm}${stDate}${endDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            appVersion: Constants.av,
            platform: Constants.pt,
            company: Constants.company,
            program: Constants.program,
          },
        }
      )
      .then((res) => {
        let dt = res.data;
        if (dt.result === "success") {
          setReportData(dt.output.results);
          setTotalPages(dt.output.totalPages);
        } else {
          setReportData([]);
          setTotalPages(0);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };
  
  /* --------------------------------------------------------
     Export Data
  --------------------------------------------------------- */
  const download = (data) => {
    setDownloadLoading(true);

    const searchTerm =
      data?.mobile && data.mobile !== "" ? `&searchTerm=${data.mobile}` : "";
  
    const status = data?.status ? `&status=${data.status}` : "&status=PENDING";
  
    let stDate = "";
    let endDate = "";
  
    // ✅ FIXED: Full-day range for filtering
    if (data?.startDate && data?.endDate) {
      stDate = `&start=${moment(data.startDate)
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss")}`;
  
      endDate = `&end=${moment(data.endDate)
        .endOf("day")
        .format("YYYY-MM-DD HH:mm:ss")}`;
    }

    axios
      .get(
        Constants.baseUrl +
        `anomaly/findAnomaly?sortBy=createdDateTime&sortAsc=true${status}${searchTerm}${stDate}${endDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            appVersion: Constants.av,
            platform: Constants.pt,
            company: Constants.company,
            program: Constants.program,
          },
          responseType: "blob",
        }
      )
      .then((res) => {
        const fileData = new Blob([res.data], { type: "text/csv" });
        saveAs(fileData, "FindAnomaly.csv");
        setDownloadLoading(false);
      })
      .catch(() => setDownloadLoading(false));
  };

  /* --------------------------------------------------------
     APPROVAL MODAL (Formik)
  --------------------------------------------------------- */

  // Get current status from selected record
  // derive current status from the selectedRecord (default to PENDING if none)
  const currentStatus = selectedRecord?.status || "PENDING";

  // all options (canonical)
  const statusOptions = [
    { value: "APPROVED", label: "Approve" },
    { value: "REJECTED", label: "Reject" },
    { value: "PENDING", label: "Pending" }
  ];

  // explicit mapping to control both which options show AND their order
  const allowedOrderMap = {
    REJECTED: ["APPROVED", "PENDING"],
    APPROVED: ["REJECTED", "PENDING"],
    PENDING: ["REJECTED", "APPROVED"]
  };

  // build filteredOptions in the exact order you specified
  const filteredOptions = (allowedOrderMap[currentStatus] || [])
    .map((val) => statusOptions.find((o) => o.value === val))
    .filter(Boolean); // guard against missing entries


  const modalFormik = useFormik({
    initialValues: {
      status: "",
      remarks: "",
    },

    validate: (values) => {
      let errors = {};

      if (!values.status) {
        errors.status = "Status required";
      }

      if (values.status === "REJECTED" && !values.remarks) {
        errors.remarks = "Remarks required";
      }

      return errors;
    },

    onSubmit: (values) => {
      handleStatusUpdate(values);
    },
  });

  /* --------------------------------------------------------
     PATCH Request (Approve / Reject)
  --------------------------------------------------------- */
  const handleStatusUpdate = (formValues) => {
    if (!selectedRecord) return;

    setLoading(true);

    axios
      .patch(
        Constants.baseUrl +
        `anomaly/approval/${selectedRecord.id}`,
        {
          status: formValues.status,
          remarks: formValues.remarks,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            appVersion: Constants.av,
            platform: Constants.pt,
            company: Constants.company,
            program: Constants.program,
            "Content-Type": "application/json",
          },
        }
      )
      .then((res) => {
        swal({
          text: "Updated Successfully",
          icon: "success",
          timer: 2000,
          buttons: false,
        });

        setModalOpen(false);
        modalFormik.resetForm();
        getAnomalyData(filterData, currentPage);
      })
      .catch((err) => {
        swal("Error!", "Unable to update.", "error");
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      {loading ? <Oval /> : null}
      <Navbar />
      {/* FINAL WORKING MODAL */}
      <div
        className={`modal fade modal-blue ${modalOpen ? "show" : ""}`}
        style={{ display: modalOpen ? "block" : "none", zIndex: 99999 }}
      >
        <Modal
          isOpen={modalOpen}
          onRequestClose={() => {
            modalFormik.resetForm();
            setModalOpen(false);
          }}
          shouldCloseOnOverlayClick={false}
          ariaHideApp={false}
          style={{
            overlay: {
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 99999,
            },
            content: {
              inset: "0px",
              border: "none",
              background: "transparent",
              overflow: "visible",
              padding: 0,
            },
          }}
        >
          <div className="modal-dialog modal-sm modal-border mx-width">
            <div className="modal-content">

              {/* HEADER */}
              <div className="modal-header">
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    modalFormik.resetForm();
                    setModalOpen(false);
                  }}
                >
                  &times;
                </button>
              </div>

              {/* BODY */}
              <div className="modal-body">
                <div className="modal-details">
                  <div className="row">

                    {/* STATUS */}
                    <div className="form-group col-lg-12">
                      <label>Status</label>
                      <select
                        className="form-control"
                        name="status"
                        value={modalFormik.values.status}
                        onChange={modalFormik.handleChange}
                      >
                        <option value="">Select</option>

                        {filteredOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>

                      {modalFormik.touched.status && modalFormik.errors.status && (
                        <div className="error-message-modal">
                          {modalFormik.errors.status}
                        </div>
                      )}
                    </div>

                    {/* REMARKS (only when rejected) */}
                    {modalFormik.values.status === "REJECTED" && (
                      <div className="form-group col-lg-12">
                        <label>Remarks</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          name="remarks"
                          value={modalFormik.values.remarks}
                          onChange={modalFormik.handleChange}
                        ></textarea>
                        {modalFormik.touched.remarks &&
                          modalFormik.errors.remarks && (
                            <div className="error-message-modal">
                              {modalFormik.errors.remarks}
                            </div>
                          )}
                      </div>
                    )}

                    {/* BUTTONS */}
                    <div className="form-group col-lg-12 text-center mt-3">
                      <button
                        className="btn btn-primary btn-sbmit"
                        onClick={modalFormik.handleSubmit}
                      >
                        Submit
                      </button>

                      <button
                        className="btn btn-reject"
                        onClick={() => {
                          modalFormik.resetForm();
                          setModalOpen(false);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </Modal>
      </div>

      <section className="page-content--bge55">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <h3 className="head1">Anomaly</h3>
            </div>
          </div>

          {/* --------------------------------------------------------
                       SEARCH CARD
          --------------------------------------------------------- */}
          <div className="card">
            <div className="card-body">
              <form onSubmit={searchFormik.handleSubmit}>
                <div className="row">
                  <div className="col-lg-8">
                    <div className="row">
                      <div className="col-md-3">
                        <input
                          className="form-control"
                          placeholder="Mobile No."
                          value={searchFormik.values.mobile}
                          onChange={(e) => {
                            const numeric = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10);
                            searchFormik.setFieldValue("mobile", numeric);
                          }}
                        />
                        {getSearchFormErrorMessage("mobile")}
                      </div>

                      <div className="col-md-3">
                        <DatePicker
                          selected={searchFormik.values.startDate}
                          onChange={(date) =>
                            searchFormik.setFieldValue("startDate", date)
                          }
                          dateFormat="dd-MM-yyyy"
                          className="form-control"
                          placeholderText="From date"
                          maxDate={new Date()}
                        />
                      </div>

                      <div className="col-md-3">
                        <DatePicker
                          selected={searchFormik.values.endDate}
                          onChange={(date) =>
                            searchFormik.setFieldValue("endDate", date)
                          }
                          dateFormat="dd-MM-yyyy"
                          className="form-control"
                          placeholderText="To date"
                          minDate={searchFormik.values.startDate || null}
                        />
                        {getSearchFormErrorMessage("endDate")}
                      </div>

                      <div className="col-md-3">
                        <select
                          className="form-control"
                          value={searchFormik.values.status}
                          onChange={searchFormik.handleChange}
                          name="status"
                        >
                          <option value="PENDING">Pending</option>
                          <option value="APPROVED">Approved</option>
                          <option value="REJECTED">Rejected</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-4">
                    <div className="d-flex justify-content-between">
                      <button type="submit" className="btn btn-primary report-submit-button">
                        Search
                      </button>

                      <button
                        type="button"
                        className="btn btn-primary report-submit-button"
                        onClick={() => {
                          searchFormik.resetForm();
                          getAnomalyData(
                            {
                              mobile: "",
                              status: "PENDING",
                              startDate: "",
                              endDate: "",
                            },
                            1
                          );
                        }}
                      >
                        Reset
                      </button>

                      {/* {reportData !== null && (
                        <button
                          type="button"
                          className="btn btn-primary report-submit-button"
                          disabled={downloadLoading}
                          onClick={() => download(searchFormik.values)}
                        >
                          {downloadLoading ? "Exporting..." : "Export"}
                        </button>
                      )} */}
                    </div>
                  </div>
                </div>
              </form>

              {/* --------------------------------------------------------
                       TABLE
              --------------------------------------------------------- */}
              <div className="row mt-20">
                <div className="col-md-12">
                  <div className="table-responsive">
                    <table className="table table-borderless table-striped table-earning">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>User ID</th>
                          <th>Name</th>
                          <th>Mobile</th>
                          <th>Purchase Value</th>
                          <th>Value</th>
                          <th>Anomaly Type</th>
                          <th>Sub Type</th>
                          <th>QR Trans Id</th>
                          <th>Claim Id</th>
                          <th>Remark</th>
                          <th>Created</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>

                      {(!reportData || reportData.length === 0) && (
                        <tbody>
                          <tr>
                            <td colSpan="14" className="text-center">
                              No record found.
                            </td>
                          </tr>
                        </tbody>
                      )}

                      {reportData && reportData.length > 0 && (
                        <tbody>
                          {reportData.map((item, index) => (
                            <tr key={item.id}>
                              <td>{(currentPage - 1) * limit + index + 1}</td>
                              <td>{item.userId}</td>
                              <td>{item.name}</td>
                              <td>{item.mobile}</td>
                              <td>{item.purchaseValue}</td>
                              <td>{item.value}</td>
                              <td>{item.anomalyType}</td>
                              <td>{item.anomalySubType}</td>
                              <td>{item.qrTransId}</td>
                              <td>{item.claimId}</td>
                              <td>{item.remarks}</td>
                              <td>
                                {moment(item.createdDateTime).format(
                                  "DD-MM-YYYY HH:mm"
                                )}
                              </td>
                              <td>{item.status}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => {
                                    setSelectedRecord(item);
                                    // Clear modal fields so user chooses new status intentionally
                                    modalFormik.setFieldValue("status", "");
                                    modalFormik.setFieldValue("remarks", "");
                                    setModalOpen(true);
                                  }}


                                >
                                  Take Action
                                </button>
                                {/* {item.status === "PENDING" ? (
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => {
                                      setSelectedRecord(item);
                                      setModalOpen(true);
                                    }}
                                  >
                                    Take Action
                                  </button>
                                ) : (
                                  "-"
                                )} */}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      )}
                    </table>

                    {totalPages > 1 && (
                      <ResponsivePagination
                        current={currentPage}
                        total={totalPages}
                        onPageChange={(page) => {
                          setCurrentPage(page);
                          getAnomalyData(filterData, page);
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />




    </>
  );
}
